import { Suspense, useLayoutEffect, useState } from 'react';

import WebApp from '@twa-dev/sdk';
import { Helmet } from 'react-helmet';

import { usePageWrapperStyle } from '@/app/hooks/use-page-wrapper-style';
import { AnalyticsServiceContextProvider } from '@/app/providers/analytics-service';
import { useAnalyticsService } from '@/app/providers/analytics-service/hooks';

import { useUser } from '@/entities/user/model/user.store';

import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { ErrorBoundaryWrapper } from '@/shared/hocs/withErrorBoundary/ErrorBoundary';
import { useFPSMeter } from '@/shared/hooks/use-fps-meter/use-fps-meter';
import { useTonConnectAnalytics } from '@/shared/hooks/use-ton-connect-analytics';
import { Loader } from '@/shared/ui/loader';

import { TonConnectProvider } from '../providers/ton-connect-provider';
import { App } from './App';

import s from './app.module.scss';

const AppLoader = () => {
  const user = useUser();
  const analyticsService = useAnalyticsService();

  useFPSMeter();
  useTonConnectAnalytics(); // Подключаем TON Connect к аналитике

  const isReady = !!user && !!analyticsService;

  return (
    <Suspense fallback={<Loader withDelayMessage />}>
      <ErrorBoundaryWrapper>{isReady ? <App /> : <Loader />}</ErrorBoundaryWrapper>
    </Suspense>
  );
};

export const AppWrapper = () => {
  const pageBackground = usePageWrapperStyle();

  const isSlowDevice = getIsSlowDevice();
  const platform = WebApp.platform;

  useLayoutEffect(() => {
    if (isSlowDevice) {
      document.documentElement.setAttribute('data-low-performance', 'true');
    } else {
      document.documentElement.removeAttribute('data-low-performance');
    }
  }, [isSlowDevice]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-platform', platform);
  }, [platform]);

  const [isAnimated, setIsAnimated] = useState(false);

  return (
    <>
      <AnalyticsServiceContextProvider>
        <TonConnectProvider>
          <AppLoader />
        </TonConnectProvider>
      </AnalyticsServiceContextProvider>
      <Helmet>
        <meta name="theme-color" content={pageBackground} />
      </Helmet>
      {!isAnimated && (
        <div
          className={s['app-wrapper-animated-overlay']}
          onAnimationEnd={() => setIsAnimated(true)}
        />
      )}
    </>
  );
};
